import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { assessmentAPI, patientAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';

// Skeleton Loader for Assessment Form
const AssessmentSkeleton = () => (
  <div className="bg-white p-8 rounded-lg w-full mx-auto animate-pulse">
    <div className="mb-8">
      <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
    </div>
    <div className="mb-12 flex items-center gap-4">
      {[1,2,3,4,5].map((_, idx) => (
        <div key={idx} className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-full bg-gray-200 mb-2"></div>
          <div className="h-3 w-16 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
      <div className="h-5 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
    </div>
    <div className="mt-8 flex justify-between items-center">
      <div className="h-10 w-32 bg-gray-200 rounded"></div>
      <div className="h-10 w-40 bg-gray-200 rounded"></div>
    </div>
    <div className="mt-4 h-4 w-1/3 bg-gray-200 rounded"></div>
    <div className="mt-4 h-4 w-1/2 bg-gray-200 rounded"></div>
  </div>
);

// Generate user-specific localStorage key
// This ensures each user has their own draft data stored separately
const getLocalStorageKey = (userId) => `adhd_assessment_draft_${userId}`;

// Utility function to clear all assessment drafts (for admin purposes)
const clearAllAssessmentDrafts = () => {
  const keys = Object.keys(localStorage);
  const draftKeys = keys.filter(key => key.startsWith('adhd_assessment_draft_'));
  draftKeys.forEach(key => localStorage.removeItem(key));
  console.log('Cleared all assessment drafts:', draftKeys.length);
};

// Utility function to get current user's draft data (for debugging)
const getCurrentUserDraft = (userId) => {
  if (!userId) return null;
  const saved = localStorage.getItem(getLocalStorageKey(userId));
  return saved ? JSON.parse(saved) : null;
};

const Assessment = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const location = useLocation();
  
  // Detect if this is doctor mode (accessed from /psy/patient/assessment/:assessmentId)
  const isDoctorMode = location.pathname.includes('/psy/patient/assessment/');
  
  const steps = ["Basic Info", "Symptoms", "Impact", "History", "Consent"];
  const [currentStep, setCurrentStep] = useState(1);
  const [basicInfo, setBasicInfo] = useState({
    age: "",
    gender: "",
    language: "",
    timezone: "",
  });
  const [symptoms, setSymptoms] = useState({});
  const [impact, setImpact] = useState({
    workPerformance: 50,
    socialRelationships: 50,
    sleepActivities: 50,
    emotionalRegulation: "",
  });
  const [history, setHistory] = useState({
    diagnosedADHD: "",
    anxietyDepression: "",
    medications: "",
    medicationsDetails: "",
    allergies: "",
    allergiesDetails: "",
    selfHarm: "",
    substanceUse: "",
  });
  const [consent, setConsent] = useState({
    clinicalAssessment: false,
    dataReview: false,
    aiTools: false,
    terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [patientProfileId, setPatientProfileId] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showReferralSuccess, setShowReferralSuccess] = useState(false);

  // Check for referral success message from location state
  useEffect(() => {
    if (location.state?.referralSuccess) {
      setShowReferralSuccess(true);
      // Clear the state to prevent showing it again
      window.history.replaceState({}, document.title);
      // Hide the message after 5 seconds
      setTimeout(() => setShowReferralSuccess(false), 5000);
    }
  }, [location.state]);

  // Get current user ID for localStorage key
  useEffect(() => {
    if (!isDoctorMode) {
      const user = tokenManager.getUser();
      const userId = user?._id || user?.id;
      setCurrentUserId(userId);
    }
  }, [isDoctorMode]);

  // Clear localStorage when user changes (for security)
  useEffect(() => {
    return () => {
      // Cleanup function - clear localStorage when component unmounts
      if (currentUserId) {
        // Only clear if we have a valid user ID
        console.log('Component unmounting, clearing localStorage for user:', currentUserId);
      }
    };
  }, [currentUserId]);

  // Add beforeunload event listener to clear localStorage when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear localStorage on page unload - let user keep their progress
      // This allows them to return and continue later
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Helper function to populate form data from database
  const populateFormFromData = (data) => {
    console.log('Populating form from data:', data);
    
    if (data.responses) {
      const responses = data.responses;
      
      // Set basic info
      if (responses.basicInfo) {
        console.log('Setting basic info:', responses.basicInfo);
        setBasicInfo({
          age: responses.basicInfo.age || "",
          gender: responses.basicInfo.gender || responses.basicInfo.gender || "",
          language: responses.basicInfo.language || "",
          timezone: responses.basicInfo.timezone || "",
        });
      }
      
      // Set symptoms
      if (responses.symptoms) {
        console.log('Setting symptoms:', responses.symptoms);
        setSymptoms(responses.symptoms);
      }
      
      // Set impact
      if (responses.impact) {
        console.log('Setting impact:', responses.impact);
        setImpact({
          workPerformance: responses.impact.workPerformance || 50,
          socialRelationships: responses.impact.socialRelationships || 50,
          sleepActivities: responses.impact.sleepActivities || 50,
          emotionalRegulation: responses.impact.emotionalRegulation || "",
        });
      }
      
      // Set history (note: could be 'History' or 'history')
      const historyData = responses.History || responses.history;
      if (historyData) {
        console.log('Setting history:', historyData);
        setHistory({
          diagnosedADHD: historyData.diagnosedADHD || "",
          anxietyDepression: historyData.anxietyDepression || "",
          medications: historyData.medications || "",
          medicationsDetails: historyData.medicationsDetails || "",
          allergies: historyData.allergies || "",
          allergiesDetails: historyData.allergiesDetails || "",
          selfHarm: historyData.selfHarm || "",
          substanceUse: historyData.substanceUse || "",
        });
      }
      
      // Set consent
      if (responses.consent) {
        console.log('Setting consent:', responses.consent);
        setConsent({
          clinicalAssessment: responses.consent.clinicalAssessment || false,
          dataReview: responses.consent.dataReview || false,
          aiTools: responses.consent.aiTools || false,
          terms: responses.consent.terms || false,
        });
      }
    }
    
    // Set assessment status
    if (data.status) {
      console.log('Setting assessment status:', data.status);
      setAssessmentStatus(data.status);
    }
  };

  // Validation functions for each step
  const validateBasicInfo = () => {
    return basicInfo.age && basicInfo.age.trim() !== "" && 
           basicInfo.gender && basicInfo.gender.trim() !== "" && 
           basicInfo.language && basicInfo.language.trim() !== "" && 
           basicInfo.timezone && basicInfo.timezone.trim() !== "";
  };

  const validateSymptoms = () => {
    const inattentionQuestions = 8; // Number of inattention questions
    const hyperactivityQuestions = 8; // Number of hyperactivity questions
    
    let inattentionAnswered = 0;
    let hyperactivityAnswered = 0;
    
    // Check inattention questions
    for (let i = 0; i < inattentionQuestions; i++) {
      const questionKey = `inattention_${i}`;
      if (symptoms[questionKey] && symptoms[questionKey].trim() !== "") {
        inattentionAnswered++;
      }
    }
    
    // Check hyperactivity questions
    for (let i = 0; i < hyperactivityQuestions; i++) {
      const questionKey = `hyperactivity_${i}`;
      if (symptoms[questionKey] && symptoms[questionKey].trim() !== "") {
        hyperactivityAnswered++;
      }
    }
    
    return inattentionAnswered === inattentionQuestions && hyperactivityAnswered === hyperactivityQuestions;
  };

  const validateImpact = () => {
    return impact.emotionalRegulation && impact.emotionalRegulation.trim() !== "";
  };

  const validateHistory = () => {
    const requiredFields = [
      'diagnosedADHD', 
      'anxietyDepression', 
      'medications', 
      'allergies', 
      'selfHarm', 
      'substanceUse'
    ];
    
    // Check if all required fields are answered
    const allAnswered = requiredFields.every(field => 
      history[field] && history[field].trim() !== ""
    );
    
    // If medications is "Yes", medicationsDetails must be filled
    const medicationsValid = history.medications !== "Yes" || 
      (history.medicationsDetails && history.medicationsDetails.trim() !== "");
    
    // If allergies is "Yes", allergiesDetails must be filled
    const allergiesValid = history.allergies !== "Yes" || 
      (history.allergiesDetails && history.allergiesDetails.trim() !== "");
    
    return allAnswered && medicationsValid && allergiesValid;
  };

  const validateConsent = () => {
    return Object.values(consent).every(Boolean);
  };

  // Get validation status for current step
  const isCurrentStepValid = () => {
    let isValid = false;
    switch (currentStep) {
      case 1:
        isValid = validateBasicInfo();
        break;
      case 2:
        isValid = validateSymptoms();
        break;
      case 3:
        isValid = validateImpact();
        break;
      case 4:
        isValid = validateHistory();
        break;
      case 5:
        isValid = validateConsent();
        break;
      default:
        isValid = false;
    }
    
    return isValid;
  };

  const handleBasicInfoChange = (e) => {
    if (isDoctorMode) return; // Read-only for doctors
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSymptomsChange = (e) => {
    if (isDoctorMode) return; // Read-only for doctors
    const { name, value } = e.target;
    setSymptoms((prev) => ({ ...prev, [name]: value }));
  };

  const handleImpactChange = (e) => {
    if (isDoctorMode) return; // Read-only for doctors
    const { name, value } = e.target;
    setImpact((prev) => ({ ...prev, [name]: value }));
  };

  const handleHistoryChange = (e) => {
    if (isDoctorMode) return; // Read-only for doctors
    const { name, value } = e.target;
    setHistory((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsentChange = (e) => {
    if (isDoctorMode) return; // Read-only for doctors
    const { name, checked } = e.target;
    setConsent((prev) => ({ ...prev, [name]: checked }));
  };

  const nextStep = () => {
    if (isCurrentStepValid()) {
    setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Mark assessment as reviewed (for doctor mode)
  const markAsReviewed = async () => {
    if (!assessmentData) {
      setError('Assessment data not loaded.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const currentDate = new Date().toISOString();
      const updateData = {
        patientId: assessmentData.patient,
        responses: assessmentData.responses,
        reviewedAt: currentDate,
        type: 'follow_up',
        submitted: true
      };
      
      const response = await assessmentAPI.createOrUpdateAssessment(updateData);
      console.log("Assessment marked as reviewed:", response);
      
      // Update local state
      setAssessmentStatus('reviewed');
      setAssessmentData(prev => ({ 
        ...prev, 
        status: 'reviewed', 
        reviewedAt: currentDate,
        type: 'follow_up'
      }));
      
      // Show success message or navigate
      toast.success('Assessment has been successfully reviewed ', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/psy/dashboard');
    } catch (error) {
      console.error("Error marking assessment as reviewed:", error);
      toast.error('Unable to update assessment status. Please try again or contact support if the issue persists.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setError('Failed to mark assessment as reviewed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit assessment function
  const submitAssessment = async () => {
    if (!isCurrentStepValid()) {
      toast.error('Please complete all required fields before submitting.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setError('Please complete all required fields before submitting.');
      return;
    }

    if (!patientProfileId) {
      toast.error('Patient profile not found. Please try refreshing the page.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setError('Patient profile not found. Please try refreshing the page.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const assessmentData = {
        patientId: patientProfileId,
        type: 'initial',
        responses: {
          basicInfo,
          symptoms,
          impact,
          History: history,
          consent
        }
      };

      console.log('Submitting assessment data:', assessmentData);

      const response = await assessmentAPI.createOrUpdateAssessment(assessmentData);
      
      if (response.success) {
        console.log('Assessment submitted successfully:', response);
        setAssessmentStatus('submitted');
        
        // Show success toast
        toast.success('Assessment submitted successfully! Redirecting to results...', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Clear localStorage after successful submission
        // This ensures the draft data is removed once the assessment is submitted
        if (currentUserId) {
          localStorage.removeItem(getLocalStorageKey(currentUserId));
          console.log('Cleared localStorage for user:', currentUserId);
        }
        
        // Navigate to assessment results with the assessment ID
        const assessmentId = response.assessment?._id || response.assessment?.id;
        if (assessmentId) {
          navigate(`/assessment-results/${assessmentId}`);
        } else {
          navigate('/assessment-results');
        }
      } else {
        toast.error('Failed to submit assessment. Please try again.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setError('Failed to submit assessment. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting assessment:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      toast.error(`Failed to submit assessment: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setError('Failed to submit assessment: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch assessment data and patient profile - ONLY GET API calls
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      
      try {
        // Scenario 1: If we have an assessmentId in URL, fetch that specific assessment
        if (assessmentId) {
          console.log('Fetching assessment by ID:', assessmentId);
          const res = await assessmentAPI.getAssessmentById(assessmentId);
          const a = res.assessment;
          console.log('Assessment data received:', a);
          setAssessmentData(a);
          
          // Set patient profile ID from assessment
          if (a.patient) {
            setPatientProfileId(a.patient);
          }
          
          // Populate form with fetched data
          populateFormFromData(a);
          setLoading(false);
        } 
        // Scenario 2: For patient mode, fetch patient profile first, then assessment
        else if (!isDoctorMode) {
          try {
            // Get the current user ID from tokenManager
            const currentUser = require('../../services/tokenManager').tokenManager.getUser();
            const userId = currentUser?._id || currentUser?.id;
            
            if (!userId) {
              setError('User ID not found. Please log in again.');
              setLoading(false);
              return;
            }
            
            console.log('Using user ID for assessment fetch:', userId);
            
            // Fetch assessment using user ID (backend will handle finding the patient profile)
            try {
              console.log('Fetching assessment for user ID:', userId);
              const assessmentResponse = await assessmentAPI.getAssessmentByPatientId(userId);
              console.log('Assessment response:', assessmentResponse);
              
              // Handle different response structures
              let assessmentData = null;
              
              if (assessmentResponse.data && assessmentResponse.data.assessments && assessmentResponse.data.assessments.length > 0) {
                // New response structure: data.assessments[0]
                assessmentData = assessmentResponse.data.assessments[0];
                console.log('Found assessment data in assessments array:', assessmentData);
              } else if (assessmentResponse.assessment) {
                // Old response structure: data.assessment
                assessmentData = assessmentResponse.assessment;
                console.log('Found assessment data in assessment field:', assessmentData);
              } else if (assessmentResponse.data && assessmentResponse.data.assessment) {
                // Alternative structure: data.data.assessment
                assessmentData = assessmentResponse.data.assessment;
                console.log('Found assessment data in data.assessment:', assessmentData);
              }
              
              if (assessmentData) {
                console.log('Setting form data from patient assessment:', assessmentData);
                setAssessmentData(assessmentData);
                
                // Set patient profile ID from the response
                if (assessmentResponse.data && assessmentResponse.data.patient) {
                  setPatientProfileId(assessmentResponse.data.patient.id);
                  console.log('Set patient profile ID from response:', assessmentResponse.data.patient.id);
                }
                
                // Populate form with fetched data
                populateFormFromData(assessmentData);
              } else {
                console.log('No assessment data found in response - new patient');
                console.log('Full assessment response structure:', assessmentResponse);
                
                // For new patients, we need to get the patient profile ID
                if (assessmentResponse.data && assessmentResponse.data.patient) {
                  setPatientProfileId(assessmentResponse.data.patient.id);
                  console.log('Set patient profile ID for new patient:', assessmentResponse.data.patient.id);
                }
              }
            } catch (assessmentError) {
              // This is normal for new patients - no existing assessment found
              console.log('No existing assessment found for patient:', assessmentError.message);
              
              // For new patients, we need to get the patient profile ID from the error response or fetch it separately
              try {
                const profileResponse = await patientAPI.getMyProfile();
                if (profileResponse && profileResponse._id) {
                  setPatientProfileId(profileResponse._id);
                  console.log('Set patient profile ID for new patient from profile API:', profileResponse._id);
                }
              } catch (profileError) {
                console.error('Error fetching patient profile for new patient:', profileError);
              }
            }
          } catch (profileError) {
            console.error('Error in patient mode data fetching:', profileError);
            setError('Failed to load patient data. Please try again.');
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data.');
        setLoading(false);
      }
    }
    
    fetchData();
  }, [assessmentId, isDoctorMode]);

  // On mount, auto-restore from draft if it exists (no prompt)
  useEffect(() => {
    if (!isDoctorMode && currentUserId) {
      const saved = localStorage.getItem(getLocalStorageKey(currentUserId));
      if (saved) {
        try {
          const data = JSON.parse(saved);
          console.log('Restoring draft data for user:', currentUserId, data);
          setBasicInfo(data.basicInfo || {});
          setSymptoms(data.symptoms || {});
          setImpact(data.impact || {});
          setHistory(data.history || {});
          setConsent(data.consent || {});
          setCurrentStep(data.currentStep || 1);
        } catch (e) {
          console.error('Error parsing saved draft data:', e);
          // Ignore parse errors
        }
      } else {
        console.log('No saved draft found for user:', currentUserId);
      }
    }
  }, [isDoctorMode, currentUserId]);

  // Remove Save Progress button and related UI. Implement auto-save on state change.
  useEffect(() => {
    if (!isDoctorMode && currentUserId) {
      const draft = {
        basicInfo,
        symptoms,
        impact,
        history,
        consent,
        currentStep
      };
      localStorage.setItem(getLocalStorageKey(currentUserId), JSON.stringify(draft));
      console.log('Auto-saved draft for user:', currentUserId, 'at step:', currentStep);
      setSaveSuccess(true);
      const timeout = setTimeout(() => setSaveSuccess(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [basicInfo, symptoms, impact, history, consent, currentStep, isDoctorMode, currentUserId]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoForm data={basicInfo} onChange={handleBasicInfoChange} isReadOnly={isDoctorMode} />
        );
      case 2:
        return <SymptomsStep data={symptoms} onChange={handleSymptomsChange} isReadOnly={isDoctorMode} />;
      case 3:
        return <ImpactStep data={impact} onChange={handleImpactChange} isReadOnly={isDoctorMode} />;
      case 4:
        return <HistoryStep data={history} onChange={handleHistoryChange} isReadOnly={isDoctorMode} />;
      case 5:
        return <ConsentStep data={consent} onChange={handleConsentChange} isReadOnly={isDoctorMode} />;
      default:
        return <div>Unknown Step</div>;
    }
  };

  if (loading) {
    return <AssessmentSkeleton />;
  }
  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }
  if (assessmentStatus === 'reviewed' && location.pathname.includes('/psy/patient/assessment/')) {
    return <div className="p-8 text-center text-green-600">This assessment has been reviewed and can no longer be edited.</div>;
  }

  return (
    <div className="max-w-full w-full mx-auto px-2 sm:px-4 py-8">
      {/* Success Message for Referral */}
      {showReferralSuccess && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg flex items-center max-w-md">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Referral submitted successfully! Please complete your assessment below.</span>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow p-4 sm:p-8">
        <div className="mb-8">
          <p className="flex items-center text-sm text-gray-600 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
            <svg
              className="w-5 h-5 mr-3 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            {isDoctorMode 
              ? "Reviewing patient assessment. This is a read-only view."
              : "Please answer honestly and thoroughly. Your answers will be reviewed by a psychiatrist and assist in your ADHD diagnosis."
            }
          </p>
        </div>

        <div className="mb-12">
          <div className="flex items-center md:gap-0 gap-2 overflow-x-auto md:overflow-visible flex-nowrap md:flex-wrap scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <div className="relative w-20 flex-shrink-0 md:w-20 md:min-w-0 min-w-[64px] sm:min-w-[80px]">
                  <div
                    className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center text-base md:text-lg transition-colors duration-300 ${
                      index + 1 === currentStep
                        ? "bg-blue-600 text-white"
                        : index + 1 < currentStep
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-500 border-2 border-gray-300"
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <svg
                        className="w-5 h-5 md:w-6 md:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <p
                    className={`absolute top-full w-full mt-2 text-xs md:text-sm text-center ${
                      index + 1 === currentStep
                        ? "text-blue-600 font-semibold"
                        : "text-gray-500"
                    } ${index + 1 === currentStep ? '' : 'truncate md:truncate-none'}`}
                    style={{ maxWidth: '80px' }}
                  >
                    {step}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 md:w-8 min-w-[16px] sm:min-w-[32px] transition-colors duration-300 ${
                      index < currentStep - 1 ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl  shadow-sm border border-gray-100">
          {renderStepContent()}
        </div>

        {/* Save Progress Button BELOW the form */}
        {/* (Removed Save Progress button, only show green message if saveSuccess) */}
        {!isDoctorMode && saveSuccess && (
          <div className="mt-6 flex flex-col items-end">
            <span className="text-green-600 text-sm">Progress saved! You can return and continue anytime.</span>
          </div>
        )}

        {/* Validation Status Indicator */}
        {!isCurrentStepValid() && !loading && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 text-sm flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.21 3.03-1.742 3.03H4.42c-1.532 0-2.492-1.696-1.742-3.03l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Please complete all required fields to continue
            </p>
          </div>
        )}

        <div className="mt-8 flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
            Back
          </button>
          {currentStep < steps.length ? (
            <button
              onClick={nextStep}
              disabled={!isCurrentStepValid() || loading}
              className={`px-8 py-3 rounded-lg flex items-center transition-all ${
                isCurrentStepValid() && !loading
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          ) : (
            (isDoctorMode || currentStep === steps.length) && (
            <button
              onClick={isDoctorMode ? markAsReviewed : submitAssessment}
                disabled={isDoctorMode ? false : (!isCurrentStepValid() || loading)}
                className={`px-8 py-3 rounded-lg flex items-center transition-all ${
                  isCurrentStepValid() && !loading
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isDoctorMode ? 'Marking as Reviewed...' : 'Submitting...'}
                </>
              ) : (
                isDoctorMode ? 'Mark as Reviewed' : 'Submit Assessment'
              )}
            </button>
            )
          )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        
      </div>
    </div>
  );
};

const BasicInfoForm = ({ data, onChange, isReadOnly }) => {
  const ageRanges = ["18-24", "25-34", "35-44", "45-54", "55+"];
  const languages = ["English", "Spanish", "French", "German"];
  const timezones = [
    "UTC-12:00", "UTC-11:00", "UTC-10:00", "UTC-09:00", "UTC-08:00",
    "UTC-07:00", "UTC-06:00", "UTC-05:00", "UTC-04:00", "UTC-03:00",
    "UTC-02:00", "UTC-01:00", "UTC+00:00", "UTC+01:00", "UTC+02:00",
    "UTC+03:00", "UTC+04:00", "UTC+05:00", "UTC+06:00", "UTC+07:00",
    "UTC+08:00", "UTC+09:00", "UTC+10:00", "UTC+11:00", "UTC+12:00",
  ];

  const isFieldValid = (fieldName) => {
    return data[fieldName] && data[fieldName].trim() !== "";
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800">Basic Information</h2>
      <p className="text-gray-600 mt-2 mb-8">
        Please provide your basic information to help us understand your
        situation better.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Age *
          </label>
          <select
            name="age"
            value={data.age}
            onChange={onChange}
            className={`w-full p-3 border rounded-lg ${
              isFieldValid('age') 
                ? 'border-green-300 bg-green-50' 
                : 'border-gray-300 focus:border-blue-500'
            }`}
            disabled={isReadOnly}
          >
            <option value="">Select age</option>
            {ageRanges.map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </select>
          {!isFieldValid('age') && (
            <p className="text-red-500 text-sm mt-1">Please select your age</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender at Birth *
          </label>
          <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-8">
            <label className="flex items-center">
              <input
                type="radio"
                name="gender"
                value="Male"
                checked={data.gender === "Male"}
                onChange={onChange}
                className="form-radio h-4 w-4 text-blue-600"
                disabled={isReadOnly}
              />
              <span className="ml-2 text-gray-700">Male</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="gender"
                value="Female"
                checked={data.gender === "Female"}
                onChange={onChange}
                className="form-radio h-4 w-4 text-blue-600"
                disabled={isReadOnly}
              />
              <span className="ml-2 text-gray-700">Female</span>
            </label>
            <label className="flex items-center">fetchNurseProfile
              <input
                type="radio"
                name="gender"
                value="Prefer not to say"
                checked={data.gender === "Prefer not to say"}
                onChange={onChange}
                className="form-radio h-4 w-4 text-blue-600"
                disabled={isReadOnly}
              />
              <span className="ml-2 text-gray-700">Prefer not to say</span>
            </label>
          </div>
          {!isFieldValid('gender') && (
            <p className="text-red-500 text-sm mt-1">Please select your gender</p>
          )}
        </div>


        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Language Spoken *
          </label>
          <select
            name="language"
            value={data.language}
            onChange={onChange}
            className={`w-full p-3 border rounded-lg ${
              isFieldValid('language') 
                ? 'border-green-300 bg-green-50' 
                : 'border-gray-300 focus:border-blue-500'
            }`}
            disabled={isReadOnly}
          >
            <option value="">Select language</option>
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
          {!isFieldValid('language') && (
            <p className="text-red-500 text-sm mt-1">Please select your language</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Timezone *
          </label>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <select
              name="timezone"
              value={data.timezone}
              onChange={onChange}
              className={`w-full p-3 border rounded-lg ${
                isFieldValid('timezone') 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 focus:border-blue-500'
              }`}
              disabled={isReadOnly}
            >
              <option value="">Select timezone</option>
              {timezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            {/* <button className="px-4 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 whitespace-nowrap" disabled={isReadOnly}>
              Auto-detect
            </button> */}
          </div>
          {!isFieldValid('timezone') && (
            <p className="text-red-500 text-sm mt-1">Please select your timezone</p>
          )}
        </div>
      </div>
    </div>
  );
};

const SymptomsStep = ({ data, onChange, isReadOnly }) => {
  const inattentionQuestions = [
    "I have trouble sustaining attention in tasks or play activities",
    "I often lose things necessary for tasks or activities",
    "I frequently forget to complete tasks",
    "I have difficulty following through on instructions",
    "I often avoid tasks that require sustained mental effort",
    "I struggle to organize tasks and activities",
    "I become easily distracted by unrelated stimuli",
    "I often forget important appointments or deadlines",
  ];

  const hyperactivityQuestions = [
    "I often fidget or tap my hands/feet",
    "I have difficulty staying seated when expected to",
    "I often talk excessively",
    "I have trouble waiting my turn",
    "I often interrupt others",
    "I frequently engage in physical activities at inappropriate times",
    "I have difficulty playing or engaging in leisure activities quietly",
    "I often blurt out answers before questions are completed",
  ];

  const options = ["Never", "Rarely", "Sometimes", "Often", "Very Often"];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800">Symptoms Assessment</h2>
      <p className="text-gray-600 mt-1 mb-8">
        Based on DSM-5 criteria, please indicate how frequently you experience
        the following symptoms.
      </p>

      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Inattention Symptoms
        </h3>
        <div className="space-y-2">
          {inattentionQuestions.map((question, index) => (
            <QuestionRow
              key={`inattention-${index}`}
              question={question}
              questionId={`inattention_${index}`}
              data={data}
              onChange={onChange}
              options={options}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Hyperactivity/Impulsivity Symptoms
        </h3>
        <div className="space-y-2">
          {hyperactivityQuestions.map((question, index) => (
            <QuestionRow
              key={`hyperactivity-${index}`}
              question={question}
              questionId={`hyperactivity_${index}`}
              data={data}
              onChange={onChange}
              options={options}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const QuestionRow = ({ question, questionId, data, onChange, options, isReadOnly }) => {
  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center p-3 rounded-lg hover:bg-gray-50 -mx-3">
      <p className="text-sm text-gray-800 w-full md:w-2/5 pr-0 md:pr-4 mb-2 md:mb-0">{question}</p>
      <div className="flex flex-wrap md:flex-nowrap items-center space-x-4 md:space-x-8 w-full md:w-auto">
        {options.map((option) => (
          <label
            key={option}
            className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer mb-2 md:mb-0"
          >
            <input
              type="radio"
              name={questionId}
              value={option}
              checked={data[questionId] === option}
              onChange={onChange}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={isReadOnly}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const ImpactStep = ({ data, onChange, isReadOnly }) => {
  const emotionalRegulationOptions = [
    "Never", "Rarely", "Sometimes", "Often", "Very Often",
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800">Daily Life Impact</h2>
      <p className="text-gray-600 mt-1 mb-8">
        Please tell us how ADHD symptoms affect different areas of your life.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <ImpactSlider
          title="Work & Academic Performance"
          question="How much does it affect your performance?"
          name="workPerformance"
          value={data.workPerformance}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />
        <ImpactSlider
          title="Social & Family Relationships"
          question="How much does it affect your relationships?"
          name="socialRelationships"
          value={data.socialRelationships}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />
        <ImpactSlider
          title="Sleep & Daily Activities"
          question="How much does it affect your sleep and daily routine?"
          name="sleepActivities"
          value={data.sleepActivities}
          onChange={onChange}
          isReadOnly={isReadOnly}
        />

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800">Emotional Regulation</h4>
          <p className="text-sm text-gray-600 mt-1">
            How often do you feel overwhelmed or frustrated?
          </p>
          <div className="mt-6">
            <select
              name="emotionalRegulation"
              value={data.emotionalRegulation}
              onChange={onChange}
              className={`w-full p-3 border rounded-lg ${
                data.emotionalRegulation 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 focus:border-blue-500'
              }`}
              disabled={isReadOnly}
            >
              <option value="">Select frequency</option>
              {emotionalRegulationOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {!data.emotionalRegulation && (
              <p className="text-red-500 text-sm mt-1">Please select how often you feel overwhelmed</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ImpactSlider = ({ title, question, name, value, onChange, isReadOnly }) => {
  const progress = (value / 100) * 100;
  const sliderStyle = {
    background: `linear-gradient(to right, #3b82f6 ${progress}%, #e5e7eb ${progress}%)`,
  };

  return (
    <>
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          background: #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          margin-top: -4px;
        }
        .slider-thumb::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #3b82f6;
          border-radius: 50%;
          cursor: pointer;
        }
      `}</style>
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-800">{title}</h4>
        <p className="text-sm text-gray-600 mt-1">{question}</p>
        <div className="mt-8">
          <input
            type="range"
            min="0"
            max="100"
            name={name}
            value={value}
            onChange={onChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
            style={sliderStyle}
            disabled={isReadOnly}
          />
          <div className="flex justify-between text-sm text-gray-500 mt-2">
            <span>Not at all</span>
            <span>Severely</span>
          </div>
        </div>
      </div>
    </>
  );
};

const HistoryStep = ({ data, onChange, isReadOnly }) => {
  const questions = [
    {
      id: "diagnosedADHD",
      text: "Have you been previously diagnosed with ADHD?",
    },
    {
      id: "anxietyDepression",
      text: "Do you have a history of anxiety or depression?",
    },
    {
      id: "medications",
      text: "Do you take any prescription medications?",
      detailsId: "medicationsDetails",
    },
    {
      id: "allergies",
      text: "Any known allergies or reactions to meds?",
      detailsId: "allergiesDetails",
    },
    {
      id: "selfHarm",
      text: "Have you ever had thoughts of self-harm or suicide?",
      warning: true,
    },
    {
      id: "substanceUse",
      text: "Any substance use (alcohol, nicotine, cannabis)?",
    },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800">
        Medical & Mental Health History
      </h2>
      <p className="text-gray-600 mt-1 mb-8">
        Your history helps us understand possible co-occuring conditions or risk
        factors.
      </p>
      <div className="space-y-6">
        {questions.map((q) => (
          <HistoryQuestionRow
            key={q.id}
            questionId={q.id}
            questionText={q.text}
            detailsId={q.detailsId}
            value={data[q.id]}
            detailsValue={q.detailsId ? data[q.detailsId] : ""}
            onChange={onChange}
            hasWarning={q.warning}
            isReadOnly={isReadOnly}
          />
        ))}
      </div>
    </div>
  );
};

const HistoryQuestionRow = ({
  questionId,
  questionText,
  detailsId,
  value,
  detailsValue,
  onChange,
  hasWarning,
  isReadOnly,
}) => {
  const showDetails = !!detailsId;
  const isFieldValid = () => {
    if (!value) return false;
    if (showDetails && value === "Yes" && !detailsValue) return false;
    return true;
  };
  
  return (
    <div className={`p-5 border rounded-lg hover:bg-gray-50 transition-colors ${
      isFieldValid() ? 'border-green-200 bg-green-50' : 'border-gray-200'
    }`}>
      <div className="flex items-center">
        <p className="text-sm text-gray-800 mr-2">{questionText}</p>
        {hasWarning && (
          <svg
            className="w-5 h-5 text-red-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.21 3.03-1.742 3.03H4.42c-1.532 0-2.492-1.696-1.742-3.03l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name={questionId}
              value="Yes"
              checked={value === "Yes"}
              onChange={onChange}
              className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              disabled={isReadOnly}
            />
            <span className="ml-2 text-sm text-gray-700">Yes</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name={questionId}
              value="No"
              checked={value === "No"}
              onChange={onChange}
              className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              disabled={isReadOnly}
            />
            <span className="ml-2 text-sm text-gray-700">No</span>
          </label>
        </div>
        <div className="w-56">
          {showDetails && (
            <input
              type="text"
              name={detailsId}
              value={detailsValue}
              onChange={onChange}
              placeholder="Please specify"
              className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-600 transition-all ${
                value === "Yes" && !detailsValue ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={isReadOnly}
            />
          )}
        </div>
      </div>
      {!isFieldValid() && (
        <p className="text-red-500 text-sm mt-2">
          {!value ? 'Please answer this question' : 
           (showDetails && value === "Yes" && !detailsValue ? 'Please provide details' : '')}
        </p>
      )}
    </div>
  );
};

const ConsentStep = ({ data, onChange, isReadOnly }) => {
  const consentItems = [
    {
      id: "clinicalAssessment",
      text: "I understand this is a clinical assessment, not a final diagnosis.",
    },
    {
      id: "dataReview",
      text: "My data will be securely reviewed by licensed professionals.",
    },
    {
      id: "aiTools",
      text: "I consent to the use of AI tools for initial analysis.",
    },
    { id: "terms", text: "I accept the terms and privacy policy" },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800">
        Consent & Acknowledgement
      </h2>
      <p className="text-gray-600 mt-1 mb-8">
        Before submitting, please review and accept the following terms.
      </p>
      <div className="space-y-4">
        {consentItems.map((item) => (
          <label
            key={item.id}
            className={`flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer ${
              data[item.id] ? 'border-green-200 bg-green-50' : 'border-gray-200'
            }`}
          >
            <input
              type="checkbox"
              name={item.id}
              checked={data[item.id]}
              onChange={onChange}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={isReadOnly}
            />
            <span className="ml-4 text-sm text-gray-800">{item.text}</span>
          </label>
        ))}
      </div>
      {!Object.values(data).every(Boolean) && (
        <p className="text-red-500 text-sm mt-4">
          Please accept all terms to continue
        </p>
      )}
    </div>
  );
};

export default Assessment;